import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import api from '../../utils/api';

export default function MemberAnnouncements() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) {
      navigate('/member-portal/login');
      return;
    }
    fetchAnnouncements();
  }, [page, navigate]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/member-portal/announcements', {
        params: {
          page,
          limit: 10,
        },
      });
      setMessages(response.data.data || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch announcements');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Announcements
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : messages.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No announcements found
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper>
            <List>
              {messages.map((message) => (
                <ListItem
                  key={message.id}
                  button
                  divider
                  onClick={() => setSelectedMessage(message)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {message.title || 'Announcement'}
                        </Typography>
                        {message.status && (
                          <Chip
                            label={message.status}
                            color={getStatusColor(message.status)}
                            size="small"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(message.created_at)}
                        {message.delivered_at && ` • Delivered: ${formatDate(message.delivered_at)}`}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          <Dialog
            open={!!selectedMessage}
            onClose={() => setSelectedMessage(null)}
            maxWidth="sm"
            fullWidth
          >
            {selectedMessage && (
              <>
                <DialogTitle>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    {selectedMessage.title || 'Announcement'}
                    {selectedMessage.status && (
                      <Chip
                        label={selectedMessage.status}
                        color={getStatusColor(selectedMessage.status)}
                        size="small"
                      />
                    )}
                  </Box>
                </DialogTitle>
                <DialogContent dividers>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {formatDate(selectedMessage.created_at)}
                    {selectedMessage.delivered_at && (
                      <> • Delivered: {formatDate(selectedMessage.delivered_at)}</>
                    )}
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedMessage.message || 'No content.'}
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setSelectedMessage(null)}>Close</Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
}
